import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ReadonlyIcon } from './ReadonlyIcon';
import { usePopupRef } from '@framework/hooks';
import { classNames } from '@framework/utils';
import { IconButton } from './IconButton';
import { MenuButton } from './MenuButton';
import { useUndoRedoHandlers } from './useUndoRedoHandlers';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import { useModalHandlers } from './useModalHandlers';
import { ViewModelSettingModal, ViewModelDeleteConfirmModal } from '@view-model/ui/components/ViewModel';
import { SidebarOpenButton, useSidebarContext } from '@user/Sidebar';
import { useCurrentUserId, useUserType } from '@framework/auth';
import { faCopy, faObjectGroup, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import {
  faCog,
  faEllipsisH,
  faLink,
  faReply,
  faStar,
  faLock,
  faClock,
  faGlobe,
  faUser,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { useBookmark } from '../../../hooks';
import type { WorkspaceEntity } from '@workspace/domain/workspace';
import { ViewModelClipboardOperator } from '@view-model/application/clipboard';
import { toast } from 'react-hot-toast';
import { useWorkspaceMemberRoles } from '@workspace/domain/workspace/useWorkspaceMemberRoles';
import { ClipboardUtils } from '@framework/ClipboardUtils';
import { usePersistedObject, usePrimitiveValueListener } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { GroupLicense } from '@group/domain';
import { ExportImageButtons } from './ExportImageButtons/ExportImageButtons';
import { ConfigureIndividualPermissions } from '@workspace/view-model-folder/ui/modals/individual-permissions/ConfigureIndividualPermissions';
import { useViewModelIndividualPermissionEnabled } from '@workspace/domain/useViewModelIndividualPermissionEnabled';
import { useGroupLicensePermissions } from '@group/License';

type Props = {
  workspace: WorkspaceEntity;
  viewModel: ViewModelEntity;
  viewModelPermissions: { isEditable: boolean; isContentEditable: boolean; isDeletable: boolean };
  onGenerateLink(): void;
  onOpenTimer(): void;
  onExportViewModelAsImage(isWhole: boolean): void;
};

export const HeaderMenuLeft: React.FC<Props> = ({
  workspace,
  viewModel,
  viewModelPermissions,
  onGenerateLink,
  onOpenTimer,
  onExportViewModelAsImage,
}: Props) => {
  const { t } = useTranslation();
  const currentUserId = useCurrentUserId();
  const userType = useUserType();
  const { isEditable, isContentEditable, isDeletable } = viewModelPermissions;
  const readonly = !isContentEditable;

  const roles = useWorkspaceMemberRoles(workspace.id);

  const [license] = usePersistedObject(GroupLicense, RTDBPath.Group.groupLicensePath(workspace.groupId));
  const permissions = useGroupLicensePermissions();
  // ビューモデルの個別権限設定の表示可否フラグ
  const showConfigureIndividualPermissions = useViewModelIndividualPermissionEnabled(workspace.id);
  const [individualPermissions] = usePrimitiveValueListener(RTDBPath.ViewModel.individualPermissionsPath(viewModel.id));

  // ビューモデルのURLをコピー
  const handleCopyLink = useCallback(async () => {
    try {
      await ClipboardUtils.copy(`${location.href}`);
      toast.success(t('viewModel.headerMenu.copyLinkSuccess'));
    } catch (e) {
      toast.error(t('common.message.copyFailed'));
    }
  }, [t]);

  // ビューモデルをコピー
  const handleCopyViewModel = useCallback(() => {
    ViewModelClipboardOperator.copy(viewModel.workspaceId, viewModel.id).then((success: boolean) => {
      if (success) {
        toast.success(t('common.message.copySuccess'));
      } else {
        toast.error(t('common.message.copyFailed'));
      }
    });
  }, [viewModel.id, viewModel.workspaceId, t]);

  // 元に戻す、やり直す
  const { handleRedo, handleUndo } = useUndoRedoHandlers();

  // [...] ボタンによるポップアップメニューの開閉状態
  const { isOpen, popupRef, handleClose, handleOpen } = usePopupRef<HTMLDivElement>(false);

  // 現在の表示位置を共有
  const handleGenerateLink = useCallback(() => {
    onGenerateLink();
    handleClose();
  }, [handleClose, onGenerateLink]);

  // ビューモデルの設定
  const {
    modalOpen: settingModalOpen,
    handleModalOpen: settingModalOpenHandler,
    handleModalClose: handleSettingModalClose,
  } = useModalHandlers();

  // ビューモデルの個別権限設定
  const {
    modalOpen: configureIndividualPermissionModalOpen,
    handleModalOpen: configureIndividualPermissionModalOpenHandler,
    handleModalClose: handleConfigureIndividualPermissionModalClose,
  } = useModalHandlers();

  // ビューモデルの削除
  const {
    modalOpen: deleteModalOpen,
    handleModalOpen: deleteModalOpenHandler,
    handleModalClose: handleDeleteModalClose,
  } = useModalHandlers();

  const handleSettingModalOpen = useCallback(() => {
    settingModalOpenHandler();
    handleClose();
  }, [settingModalOpenHandler, handleClose]);

  const handleConfigureIndividualPermissionOpen = useCallback(() => {
    configureIndividualPermissionModalOpenHandler();
    handleClose();
  }, [configureIndividualPermissionModalOpenHandler, handleClose]);

  const handleDeleteModalOpen = useCallback(() => {
    deleteModalOpenHandler();
    handleClose();
  }, [deleteModalOpenHandler, handleClose]);

  const handleOpenTimer = useCallback(() => {
    onOpenTimer();
    handleClose();
  }, [onOpenTimer, handleClose]);

  const handleExportViewModelAsImage = useCallback(
    (isWhole: boolean) => {
      if (!permissions?.imageExport) return;

      onExportViewModelAsImage(isWhole);
      handleClose();
    },
    [onExportViewModelAsImage, handleClose, permissions]
  );

  const { isSidebarOpenButtonVisible, toggleSidebar } = useSidebarContext();

  const { isBookmarked, toggleBookmark } = useBookmark(viewModel.id);

  // メニューの表示可能な横幅が狭い時に子要素のテキストを省略表示するためには、
  // flexコンテナに min-width を設定し、子要素には `truncate` を設定する必要がある。
  //
  // 参照:
  //   - https://css-tricks.com/flexbox-truncated-text/
  //       flexboxの横幅が狭くなったときに、子要素のテキストを省略表示するためのCSS解説
  //   - https://v1.tailwindcss.com/docs/word-break
  //       tailwind の truncate は、overflow,text-overflow, white-space をまとめて指定している。
  //   - https://v1.tailwindcss.com/docs/min-width
  //       tailwind の標準では min-w-xs が未定義のため、カスタム定義を追加している。
  return (
    <div className="flex min-w-0 gap-2">
      {isSidebarOpenButtonVisible && (
        <div className="pointer-events-auto flex w-12 shrink-0 items-center justify-center rounded-lg bg-white shadow-md hover:bg-gray-200">
          <SidebarOpenButton onClick={toggleSidebar} />
        </div>
      )}
      <div className="pointer-events-auto flex min-w-xs rounded-lg bg-white p-1 shadow-md">
        {/* ビューモデル名 */}
        <DarkTooltip tooltip={viewModel.name.value} placement="bottom" className="truncate p-2 font-bold">
          {userType === 'regular' && viewModel.isPublicReadable(workspace.setting) ? (
            // URL共有していることをアイコンで表現
            <FontAwesomeIcon icon={faGlobe} className="mr-2" />
          ) : userType === 'regular' && showConfigureIndividualPermissions && individualPermissions ? (
            // 個別権限設定していることをアイコンで表現
            <FontAwesomeIcon icon={faUser} className="mr-2" />
          ) : null}
          {viewModel.isLocked && <FontAwesomeIcon icon={faLock} className="mr-2" />}
          {viewModel.name.value}
        </DarkTooltip>

        {/* 閲覧権限で参照中 */}
        {readonly && <ReadonlyIcon />}

        {/* セパレータ */}
        <div className="mx-1 my-2 border-l border-gray-200" />

        {!readonly && (
          <>
            {/* Undo Redoボタン */}
            <IconButton
              icon={faReply}
              tooltip={t('viewModel.headerMenu.undo')}
              placement="bottom"
              onClick={handleUndo}
            />
            <IconButton
              icon={faReply}
              tooltip={t('viewModel.headerMenu.redo')}
              placement="bottom"
              onClick={handleRedo}
              flip="horizontal"
            />

            {/* セパレータ */}
            <div className="mx-1 my-2 border-l border-gray-200" />
          </>
        )}

        {/* コピーボタン */}
        <IconButton
          icon={faLink}
          tooltip={t('viewModel.headerMenu.copyUrl')}
          placement="bottom"
          onClick={handleCopyLink}
        />
        <IconButton
          icon={faCopy}
          tooltip={t('viewModel.copy.label')}
          placement="bottom"
          onClick={handleCopyViewModel}
        />

        {/* ブックマークボタン */}
        {/* ブックマークボタンは、レギュラーユーザーでかつワークスペースメンバーである時に表示 */}
        {currentUserId && userType === 'regular' && roles?.getRoleOf(currentUserId) && (
          <DarkTooltip
            tooltip={isBookmarked ? t('viewModel.removeBookmark.label') : t('viewModel.headerMenu.bookmark')}
            placement="bottom"
          >
            <button
              onClick={toggleBookmark}
              className={classNames(
                'h-10 w-10 cursor-pointer rounded-lg text-gray-700 hover:bg-gray-200 focus:outline-none',
                isBookmarked ? 'text-yellow-400' : 'text-gray-400'
              )}
            >
              <FontAwesomeIcon icon={faStar} />
            </button>
          </DarkTooltip>
        )}

        {/* KATA MAP ボタン */}
        {userType === 'regular' && (
          <DarkTooltip tooltip="KATA MAP" placement="bottom">
            <a
              href={UserPagePathBuilder.kataMapPage('menu')}
              className={classNames(
                'flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-gray-700 hover:bg-gray-200 focus:outline-none'
              )}
              target="_blank"
              rel="noreferrer"
            >
              <FontAwesomeIcon icon={faObjectGroup} />
            </a>
          </DarkTooltip>
        )}

        {/* さらに見るボタン */}
        <div className="relative">
          <button
            className="size-10 cursor-pointer rounded-lg hover:bg-gray-200 focus:outline-none"
            onClick={handleOpen}
            aria-label={t('viewModel.headerMenu.more')}
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </button>
          <div
            className={classNames(
              'absolute right-0 z-10 w-[23rem] rounded-lg bg-white py-2 shadow-lg',
              isOpen ? 'block' : 'hidden'
            )}
            ref={popupRef}
          >
            {isEditable && (
              <>
                <MenuButton
                  onClick={handleSettingModalOpen}
                  icon={faCog}
                  title={t('viewModel.viewModelSetting.label')}
                />
                <ViewModelSettingModal
                  isOpen={settingModalOpen}
                  onClose={handleSettingModalClose}
                  workspace={workspace}
                  viewModel={viewModel}
                />
              </>
            )}
            {isEditable && showConfigureIndividualPermissions && (
              <>
                <MenuButton
                  onClick={handleConfigureIndividualPermissionOpen}
                  iconDOM={
                    <span className="relative inline-block w-6">
                      <span className="">
                        <FontAwesomeIcon icon={faUser} />
                      </span>
                      <span className="absolute right-0 top-0 inline-block size-2 text-xs">
                        <FontAwesomeIcon icon={faPlus} />
                      </span>
                    </span>
                  }
                  title={t('viewModel.addOrRemoveMembers.label')}
                />
                <ConfigureIndividualPermissions
                  isOpen={configureIndividualPermissionModalOpen}
                  onClose={handleConfigureIndividualPermissionModalClose}
                  viewModel={viewModel}
                />
              </>
            )}
            <MenuButton onClick={handleOpenTimer} icon={faClock} title={t('viewModel.headerMenu.timer')} />
            <MenuButton
              onClick={handleGenerateLink}
              icon={faLink}
              title={t('viewModel.headerMenu.copyLinkWithCurrentLocation')}
            />
            <ExportImageButtons
              onExportViewModelAsImage={handleExportViewModelAsImage}
              disabled={!permissions?.imageExport}
            />
            {isDeletable && (
              <>
                <MenuButton
                  className="text-red-700"
                  icon={faTrashAlt}
                  title={t('common.action.delete')}
                  onClick={handleDeleteModalOpen}
                />
                <ViewModelDeleteConfirmModal
                  viewModel={viewModel}
                  isOpen={deleteModalOpen}
                  onClose={handleDeleteModalClose}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* 匿名ユーザの場合だけ、トライアル登録ページへの導線を表示する */}
      {userType === 'anonymous' && (
        <div className="pointer-events-auto flex items-center rounded-lg bg-white px-4 py-1 shadow-md">
          <a
            href="https://levii.co.jp/services/balus/?viewmodel"
            target="_blank"
            rel="noreferrer"
            className="whitespace-nowrap font-bold text-orange-500 hover:underline"
          >
            {t('viewModel.headerMenu.trialBanner')}
          </a>
        </div>
      )}

      {/* ビューモデルを公開している場合、ゲストユーザに関するヘルプページへの導線を表示する */}
      {userType === 'regular' && viewModel.isPublicReadable(workspace.setting) && (
        <div className="pointer-events-auto flex items-center rounded-lg bg-white px-4 py-1 shadow-md">
          <a
            href={`https://balus-help.levii.co.jp/hc/ja/articles/15468851519769-Balus%E3%82%92%E3%81%AF%E3%81%98%E3%82%81%E3%82%88%E3%81%86-%E7%AE%A1%E7%90%86%E8%80%85%E7%B7%A8#h_01JVNNDCF27NAYRM5NKF5HXNS6`}
            className="whitespace-nowrap font-bold text-orange-500 hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {t('viewModel.headerMenu.guestShareBanner')}
          </a>
        </div>
      )}

      {/* トライアルユーザの場合、ライセンス申請画面への導線を表示する */}
      {userType === 'regular' && license?.type === 'trial' && (
        <div className="pointer-events-auto flex items-center rounded-lg bg-white px-4 py-1 shadow-md">
          <a
            href={`/groups/${workspace.groupId}/console/license`}
            className="whitespace-nowrap font-bold text-orange-500 hover:underline"
          >
            {t('viewModel.headerMenu.upgradeBanner')}
          </a>
        </div>
      )}
    </div>
  );
};
